import { getFormErrorMessage } from "@core/utility/get-error-message";
import { PackageTransportFormRule, PackageTransportFormSchema } from "@factory/form/form.rule";
import { useFormDefaultPackageTransport } from "@factory/form/form-default-package-transport.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponsePackageTransportModel } from "@factory/model/query-model-package-transport";
import { 
  useQyDeletePackageTransport, 
  useQyGetPackageTransportById, 
  useQyUpdatePackageTransport} from "@factory/query/package-transport.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditPackageTransport() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultPackageTransport();
  const navigate = useNavigate();
  const { mapUpdatePackageTransport, mapDeletePackageTransport } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { packageTransportId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID PackageTransport
  const handleGetApiSuccess = (data: GetResponsePackageTransportModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("package", responseData?.package || "");
      setValue("transport", responseData?.transport || "");
      setValue("transportType", responseData?.transport_type || "");
      setValue("fromLocation", responseData?.from_location || "");
      setValue("toLocation", responseData?.to_location || "");
      setValue("pricePerPax", responseData?.price_per_pax);
      setValue("numPeople", responseData?.num_people);
      setValue("unit", responseData?.unit || "");
      setValue("amount", responseData?.amount);
      setValue("description", responseData?.description || "");
  };
  const {
    data: packageTransportResponse,
    isLoading,
    isError: packageTransportError,
  } = useQyGetPackageTransportById(packageTransportId || "", handleGetApiSuccess);
  const packageTransportData = packageTransportResponse?.data?.[0];

  // API UPDATE PackageTransport
  const handleUpdateApiSuccess = () => {
    showSuccess("PackageTransport updated successfully");
  };
  const { mutate: updatePackageTransport, isLoading: isEditLoading } = useQyUpdatePackageTransport(
    handleUpdateApiSuccess
  );

  // API DELETE PackageTransport
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("PackageTransport deleted successfully");
  };
  const { mutate: deletePackageTransport } = useQyDeletePackageTransport(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<PackageTransportFormSchema>({
    defaultValues: getDefault(packageTransportData),
    resolver: zodResolver(PackageTransportFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: PackageTransportFormSchema) => {
    if (!packageTransportData || !packageTransportData.code) {
      throw new Error("No packageTransport data");
    }

    const formData = mapUpdatePackageTransport(form, packageTransportData.code);
    updatePackageTransport(formData);
  };
  const handleValidateError = (err: FieldErrors<PackageTransportFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!packageTransportData || !packageTransportData.code) {
      throw new Error("No packageTransport data found");
    }

    const form = mapDeletePackageTransport(packageTransportData.code);
    deletePackageTransport(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    packageTransportId,
    packageTransportData,
    isLoading,
    packageTransportError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}